import React, { useMemo } from 'react';
import { siteUrl } from '../config/site';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import { FaWhatsapp } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

/**
 * Texte de partage aligné sur la billetterie publique (buy) :
 * « Titre · date · lieu » + URL = lien_achat / purchase_link.
 */
function buildSharePayload(event = {}) {
    const shareUrl = (
        event.purchase_link ||
        event.lien_achat ||
        siteUrl('/go')
    ).toString().trim();

    const title = (event.event_title || 'Événement').toString().trim();
    const parts = [title];
    if (event.default_date) parts.push(String(event.default_date).trim());
    if (event.place) parts.push(String(event.place).trim());
    const shareText = parts.filter(Boolean).join(' · ');

    return { shareUrl, title, shareText };
}

export default function ShareEventModal({ event, isOpen, onClose }) {
    const { shareUrl, title, shareText } = useMemo(
        () => buildSharePayload(event || {}),
        [event],
    );

    if (!isOpen || !event) return null;

    const handleNativeShare = async () => {
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title, text: shareText, url: shareUrl });
                return;
            } catch (error) {
                if (error?.name === 'AbortError') return;
            }
        }
        try {
            await navigator.clipboard.writeText(shareUrl);
        } catch {
            // ignore
        }
    };

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta property="og:title" content={title} />
                <meta property="og:description" content={shareText} />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={shareText} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={shareUrl} />
            </Helmet>

            <div
                className="inset-0 z-10 w-screen overflow-y-auto fixed bg-gray-900 bg-opacity-30 backdrop-blur-sm flex justify-center items-center"
                style={{
                    zIndex: 100,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    outline: 0,
                    left: 0,
                    margin: 0,
                }}
            >
                <div
                    className="flex items-center justify-center cxe43 cnbwt cini7 cjxg0 cys4p codu7 clbq0"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="bg-white max-w-xl c2vpa co669 caufm cb8zv ccwri crwo8 c6btv">
                        <div className="p-6 cm84d">
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="c3e4j cg12x cmpw7 cdqku cqdkw cgky2 cli41"
                                >
                                    <div className="cn8jz">Close</div>
                                    <svg className="cbm9w" width={16} height={16} viewBox="0 0 16 16">
                                        <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                                    </svg>
                                </button>

                                <div className="cydwr c6f83">
                                    <div className="text-gray-800 dark:text-gray-100 cgulq c7x0x">
                                        Promouvoir cet événement
                                    </div>
                                </div>

                                <div className="cydwr">
                                    {/* <div className="text-sm cai6b mb-3">
                                        Message prêt à partager (même format que la billetterie publique) :
                                    </div> */}
                                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 mb-2">
                                        {shareText}
                                    </div>
                                    {/* <a
                                        href={shareUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-xs text-primary-600 break-all mb-4 hover:underline"
                                    >
                                        {shareUrl}
                                    </a> */}

                                    {typeof navigator !== 'undefined' && navigator.share && (
                                        <button
                                            type="button"
                                            onClick={handleNativeShare}
                                            className="mb-4 w-full inline-flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700"
                                        >
                                            Partager…
                                        </button>
                                    )}

                                    <div className="text-sm cai6b mb-2">
                                        Ou choisissez un réseau social :
                                    </div>
                                    <div className="flex items-center justify-between cxg65 c91aq ct8yz">
                                        <FacebookShareButton url={shareUrl} quote={shareText}>
                                            <svg width={36} height={36} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g clipPath="url(#FB_36_svg__a)">
                                                    <rect width={36} height={36} rx={18} fill="#fff" />
                                                    <path
                                                        d="M18 0C8.06 0 0 8.107 0 18.11 0 27.15 6.582 34.64 15.188 36V23.345h-4.571V18.11h4.57v-3.99c0-4.538 2.688-7.046 6.8-7.046 1.968 0 4.029.354 4.029.354v4.456h-2.271c-2.236 0-2.934 1.396-2.934 2.83v3.396h4.992l-.798 5.235h-4.194V36C29.418 34.641 36 27.15 36 18.11 36 8.107 27.94 0 18 0Z"
                                                        fill="#1877F2"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="FB_36_svg__a">
                                                        <rect width={36} height={36} rx={18} fill="#fff" />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </FacebookShareButton>
                                        <TwitterShareButton url={shareUrl} title={shareText}>
                                            <svg width={36} height={36} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect width={36} height={36} rx={18} fill="#000" />
                                                <path
                                                    d="M19.332 16.928 24.544 11h-1.235l-4.526 5.147L15.17 11H11l5.466 7.784L11 25h1.235l4.78-5.436L20.83 25H25l-5.668-8.072Zm-1.692 1.924-.553-.775-4.407-6.167h1.897l3.556 4.977.554.775 4.622 6.47h-1.897l-3.772-5.28Z"
                                                    fill="#fff"
                                                />
                                            </svg>
                                        </TwitterShareButton>
                                        <LinkedinShareButton url={shareUrl} summary={shareText} title={title}>
                                            <svg width={36} height={36} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect width={36} height={36} rx={18} fill="#0065C8" />
                                                <path
                                                    d="M13.333 10A3.333 3.333 0 0 0 10 13.333v9.334A3.333 3.333 0 0 0 13.333 26h9.334A3.333 3.333 0 0 0 26 22.667v-9.334A3.333 3.333 0 0 0 22.667 10h-9.334Zm2 12.667h-2v-7.334h2v7.334Zm-1-8.179a1.172 1.172 0 0 1-1.166-1.176c0-.65.522-1.176 1.166-1.176.644 0 1.167.527 1.167 1.176 0 .65-.522 1.176-1.167 1.176Zm9 8.179h-2V18.93c0-2.246-2.666-2.076-2.666 0v3.736h-2v-7.334h2v1.177c.93-1.724 4.666-1.851 4.666 1.65v4.507Z"
                                                    fill="#fff"
                                                />
                                            </svg>
                                        </LinkedinShareButton>
                                        <WhatsappShareButton url={shareUrl} title={shareText}>
                                            <FaWhatsapp size={36} color="#25d366" />
                                        </WhatsappShareButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
